import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "../lib/api";
import { useSeo } from "../lib/seo";
import { articleUrl, categoryUrl, formatDate } from "../lib/format";
import { Pagination } from "../components/common/Pagination";
import { EmptyState } from "../components/common/States";

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const page = Number(params.get("page") || 1);
  const [input, setInput] = useState(q);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useSeo({ title: q ? `Search: ${q}` : "Search", description: "Search ArticleBlogs articles, tips and guides.", path: "/search/", robots: "noindex,follow" });
  useEffect(() => { setInput(q); }, [q]);
  useEffect(() => {
    if (!q) { setData(null); return; }
    setLoading(true);
    api.get("/search", { params: { q, page, page_size: 10 } }).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [q, page]);

  const submit = (e) => { e.preventDefault(); if (input.trim()) setParams({ q: input.trim() }); };

  return (
    <div className="container-ab max-w-4xl py-14 lg:py-20">
      <p className="eyebrow">Search</p>
      <h1 className="mt-3 font-serif text-4xl font-semibold tracking-tight sm:text-5xl">{q ? <>Results for <span className="italic text-ab-accent">“{q}”</span></> : "Find an article"}</h1>
      <form onSubmit={submit} role="search" className="mt-8 flex gap-3">
        <label htmlFor="search-input" className="sr-only">Search query</label>
        <div className="relative flex-1">
          <Search size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-ab-muted" />
          <input id="search-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Try “fridge”, “laptop”, “hotel”…" className="input-ab !pl-11" data-testid="search-page-input" />
        </div>
        <button type="submit" className="btn-primary" data-testid="search-page-submit">Search</button>
      </form>

      {q && data && !loading && <p className="mt-8 text-sm text-ab-muted" data-testid="search-result-count">{data.total} result{data.total === 1 ? "" : "s"}</p>}
      {loading && <p className="mt-8 text-sm text-ab-muted">Searching…</p>}

      {data && data.items.length > 0 && (
        <ol className="mt-6 divide-y divide-ab-border" data-testid="search-results">
          {data.items.map((a) => (
            <li key={a.id} className="py-7" data-testid={`search-result-${a.slug}`}>
              <Link to={categoryUrl(a.category_slug)} className="eyebrow">{a.category_name}</Link>
              <h2 className="mt-2 font-serif text-2xl font-semibold leading-snug"><Link to={articleUrl(a)} className="hover:text-ab-accent">{a.title}</Link></h2>
              <p className="mt-2 text-ab-muted">{a.excerpt}</p>
              <p className="mt-3 text-xs text-ab-muted">{formatDate(a.published_at)} · {a.reading_time} min read</p>
            </li>
          ))}
        </ol>
      )}
      {data && !loading && data.items.length === 0 && <div className="mt-8"><EmptyState title={`No matches for “${q}”`} text="Try a shorter word, a product name, or browse a category from the menu." /></div>}
      {data && <Pagination page={data.page} pages={data.pages} buildHref={(n) => `/search/?q=${encodeURIComponent(q)}&page=${n}`} />}
    </div>
  );
}
